import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';

import { fetchUoms } from '../forms/forms.apis';
import {
  createActivityOutput,
  fetchActivitiesList,
  fetchActivityOutputs,
  updateActivityOutput,
} from './activity.apis';
import {
  IActivityListItem,
  IActivityOutputForm,
  IActivityOutputItem,
} from './activity.models';
import { ILookupItem } from '../forms/forms.models';

type Props = {
  navigate?: (path: string) => void;
  activityix?: string;
};

type State = {
  loading: boolean;
  saving: boolean;
  error: string;
  success: string;

  activities: IActivityListItem[];
  uoms: ILookupItem[];

  selectedActivityix: string;
  outputs: IActivityOutputItem[];

  dialogOpen: boolean;
  dialogMode: 'add' | 'edit';
  form: IActivityOutputForm;
};

export default class ActivityOutputPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      saving: false,
      error: '',
      success: '',

      activities: [],
      uoms: [],

      selectedActivityix: props.activityix || '',
      outputs: [],

      dialogOpen: false,
      dialogMode: 'add',
      form: this.getEmptyForm(props.activityix || ''),
    };
  }

  componentDidMount(): void {
    this.loadInitialData();
  }

  private getEmptyForm = (activityix: string): IActivityOutputForm => {
    return {
      activityix,
      recname: '',
      shortcode: '',
      description: '',
      uomix: '',
      status: 1,
    };
  };

  private loadInitialData = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const [activitiesResult, uoms] = await Promise.all([
        fetchActivitiesList({
          search: '',
          typeix: '',
          costcenterix: '',
          status: '1',
          page: 1,
          pagesize: 1000,
        }),
        fetchUoms(),
      ]);

      const activities = activitiesResult.items || [];
      const selectedActivityix =
        this.state.selectedActivityix || activities[0]?.recix || '';

      this.setState(
        {
          activities,
          uoms,
          selectedActivityix,
          form: this.getEmptyForm(selectedActivityix),
        },
        async () => {
          if (selectedActivityix) {
            await this.loadOutputs(selectedActivityix);
          } else {
            this.setState({ loading: false });
          }
        }
      );
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load activity outputs page',
      });
    }
  };

  private loadOutputs = async (activityix: string): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
      success: '',
    });

    try {
      const result = await fetchActivityOutputs(activityix);

      this.setState({
        outputs: result.items || [],
        loading: false,
      });
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load activity outputs',
      });
    }
  };

  private handleActivityChange = (event: SelectChangeEvent<string>): void => {
    const selectedActivityix = event.target.value;

    this.setState(
      {
        selectedActivityix,
        form: this.getEmptyForm(selectedActivityix),
      },
      () => {
        if (selectedActivityix) {
          this.loadOutputs(selectedActivityix);
        }
      }
    );
  };

  private openAddDialog = (): void => {
    this.setState({
      dialogOpen: true,
      dialogMode: 'add',
      form: this.getEmptyForm(this.state.selectedActivityix),
      error: '',
      success: '',
    });
  };

  private openEditDialog = (output: IActivityOutputItem): void => {
    this.setState({
      dialogOpen: true,
      dialogMode: 'edit',
      form: {
        recix: output.recix,
        activityix: this.state.selectedActivityix,
        recname: output.recname || '',
        shortcode: output.shortcode || '',
        description: output.description || '',
        uomix: output.uom?.recix || '',
        status: output.status ?? 1,
      },
      error: '',
      success: '',
    });
  };

  private closeDialog = (): void => {
    this.setState({
      dialogOpen: false,
      form: this.getEmptyForm(this.state.selectedActivityix),
    });
  };

  private handleTextChange =
    (field: keyof IActivityOutputForm) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        form: {
          ...prevState.form,
          [field]: value,
        },
      }));
    };

  private handleSelectChange =
    (field: keyof IActivityOutputForm) =>
    (event: SelectChangeEvent<string>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        form: {
          ...prevState.form,
          [field]: field === 'status' ? Number(value) : value,
        },
      }));
    };

  private validateForm = (): string[] => {
    const { form } = this.state;
    const errors: string[] = [];

    if (!form.activityix) errors.push('Activity is required.');
    if (!form.recname.trim()) errors.push('Output name is required.');
    if (!form.shortcode.trim()) errors.push('Output code is required.');
    if (!form.description.trim()) errors.push('Description is required.');
    if (!form.uomix) errors.push('Unit of measure is required.');

    return errors;
  };

  private handleSave = async (): Promise<void> => {
    const errors = this.validateForm();

    if (errors.length > 0) {
      this.setState({
        error: errors.join(' '),
        success: '',
      });
      return;
    }

    this.setState({
      saving: true,
      error: '',
      success: '',
    });

    try {
      if (this.state.dialogMode === 'add') {
        await createActivityOutput(this.state.form);
      } else if (this.state.form.recix) {
        await updateActivityOutput(this.state.form.recix, this.state.form);
      }

      this.setState({
        saving: false,
        dialogOpen: false,
        success: 'Activity output saved successfully.',
        form: this.getEmptyForm(this.state.selectedActivityix),
      });

      if (this.state.selectedActivityix) {
        await this.loadOutputs(this.state.selectedActivityix);
      }
    } catch (error: any) {
      this.setState({
        saving: false,
        error: error?.message || 'Failed to save activity output',
      });
    }
  };

  private handleBack = (): void => {
    const path = '/forms/activities';

    if (this.props.navigate) {
      this.props.navigate(path);
      return;
    }

    window.location.href = path;
  };

  private handleRefresh = (): void => {
    if (this.state.selectedActivityix) {
      this.loadOutputs(this.state.selectedActivityix);
    }
  };

  render(): React.ReactNode {
    const {
      loading,
      saving,
      error,
      success,
      activities,
      uoms,
      selectedActivityix,
      outputs,
      dialogOpen,
      dialogMode,
      form,
    } = this.state;

    const selectedActivity =
      activities.find((x) => x.recix === selectedActivityix) || null;

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Activity Outputs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Define measurable outputs for ABB activities and future KPI linkage.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={this.handleBack}
            >
              Back
            </Button>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={this.handleRefresh}
              disabled={!selectedActivityix}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={this.openAddDialog}
              disabled={!selectedActivityix}
            >
              Add Output
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        ) : null}

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel id="activity-output-activity-label">
                    Activity
                  </InputLabel>
                  <Select
                    labelId="activity-output-activity-label"
                    value={selectedActivityix}
                    label="Activity"
                    onChange={this.handleActivityChange}
                  >
                    {activities.map((activity) => (
                      <MenuItem key={activity.recix} value={activity.recix}>
                        {activity.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    <strong>Selected:</strong> {selectedActivity?.recname || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Code:</strong> {selectedActivity?.shortcode || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Output Count:</strong> {outputs.length}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Stack spacing={2}>
          {loading ? (
            <Card>
              <CardContent>
                <Typography>Loading activity outputs...</Typography>
              </CardContent>
            </Card>
          ) : outputs.length === 0 ? (
            <Card>
              <CardContent>
                <Typography color="text.secondary">
                  No outputs defined for this activity yet.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            outputs.map((output, index) => (
              <Card key={output.recix || index}>
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {output.recname}
                      </Typography>

                      <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1 }}>
                        <Chip
                          size="small"
                          label={`Code: ${output.shortcode}`}
                        />
                        <Chip
                          size="small"
                          label={`UOM: ${output.uom?.recname || '-'}`}
                        />
                        <Chip
                          size="small"
                          color={output.status === 1 ? 'success' : 'default'}
                          label={output.status === 1 ? 'Active' : 'Disabled'}
                        />
                      </Stack>

                      <Typography variant="body2">
                        <strong>Description:</strong> {output.description || '-'}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => this.openEditDialog(output)}
                      >
                        Edit
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))
          )}
        </Stack>

        <Dialog
          open={dialogOpen}
          onClose={this.closeDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {dialogMode === 'add' ? 'Add Activity Output' : 'Edit Activity Output'}
          </DialogTitle>

          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Output Name"
                  value={form.recname}
                  onChange={this.handleTextChange('recname')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="Output Code"
                  value={form.shortcode}
                  onChange={this.handleTextChange('shortcode')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel id="output-status-label">Status</InputLabel>
                  <Select
                    labelId="output-status-label"
                    value={`${form.status}`}
                    label="Status"
                    onChange={this.handleSelectChange('status')}
                  >
                    <MenuItem value="1">Active</MenuItem>
                    <MenuItem value="0">Disabled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel id="output-uom-label">Unit of Measure</InputLabel>
                  <Select
                    labelId="output-uom-label"
                    value={form.uomix}
                    label="Unit of Measure"
                    onChange={this.handleSelectChange('uomix')}
                  >
                    {uoms.map((uom) => (
                      <MenuItem key={uom.recix} value={uom.recix}>
                        {uom.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  minRows={3}
                  value={form.description}
                  onChange={this.handleTextChange('description')}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={this.closeDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={this.handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}